import { z } from 'zod';
import { tKey } from '@strength/i18n';
import { v4 } from 'uuid';
export const parseIntNoNaN = (value) => {
    if (value === undefined)
        return null;
    const result = parseInt(value);
    if (isNaN(result)) {
        return null;
    }
    if (result.toString() !== value) {
        return null;
    }
    return result;
};
export const parseFloatNoNaN = (value) => {
    if (value === undefined)
        return null;
    // Trim trailing zeros from decimal values
    const trimmedValue = value.indexOf('.') ? value.replace(/\.0+$/, '') : value;
    const result = parseFloat(trimmedValue);
    if (isNaN(result)) {
        return null;
    }
    const resultAsString = result.toString();
    // Allow "0.5" and ".5" but not "0.5foo"
    if (resultAsString !== trimmedValue &&
        resultAsString.slice(1) !== trimmedValue) {
        return null;
    }
    return result;
};
export function removeUnits(value) {
    return value.replace(/[^0-9.-]+/g, '').trim();
}
export function getUnitFromData(data) {
    if (typeof data !== 'object')
        return '';
    if ('format' in data && data.format === 'percentage')
        return '%';
    if ('unit' in data)
        return data.unit;
    return '';
}
/**
 * Get the value that should be shown in an input for a metric. Does not work for tempo
 * @param data any valid metric data except temo
 * @returns a number or string that can be used in a text field
 */
export function getValueFromData(data) {
    if (!data || data === '') {
        return '';
    }
    if (typeof data === 'object') {
        if (data.type === 'keyword') {
            return data.value;
        }
        if (data.type === 'range') {
            if (data.max == null)
                return `${data.min}+`;
            // Render low-to-high; inverse-scale KPI ranges resolve each endpoint
            // independently and can produce min > max (see toDisplay).
            return `${Math.min(data.min, data.max)}-${Math.max(data.min, data.max)}`;
        }
        if (data.type === 'exact') {
            return Number(data.value);
        }
    }
    return Number(data);
}
export function roundDecimal(value) {
    return `${Math.round(value * 100) / 100}`;
}
export function roundDelta(value) {
    return Math.round((value + Number.EPSILON) * 100) / 100;
}
export function toDisplay(value) {
    if (value === '')
        return '';
    if (value.type === 'keyword')
        return value.value;
    const unit = 'unit' in value ? value.unit : '';
    if (value.type === 'range') {
        if (value.max == null)
            return `${roundDecimal(value.min)}+`;
        // Render low-to-high. Inverse-scale KPI ranges resolve each endpoint
        // independently (value/pct), so a min% < max% prescription produces a
        // resolved min > max; normalize here so the displayed range always reads
        // ascending. Non-inverse ranges already satisfy min <= max.
        const lo = Math.min(value.min, value.max);
        const hi = Math.max(value.min, value.max);
        return `${roundDecimal(lo)}-${roundDecimal(hi)}${unit}`;
    }
    if (value.type === 'complex') {
        return value.reps.join(value.delimiter);
    }
    return `${roundDecimal(value.value)}${unit}`;
}
export function invalidValue(ctx, message) {
    ctx.addIssue({
        code: 'custom',
        message: message[0],
        params: message[2],
    });
    return z.NEVER;
}
export function isValidNumber(value) {
    return value !== null && value !== undefined && value >= 0;
}
/**
 * Get the value or values out of a metric input.
 * If we find a valid exact or min value, an object with at least min is returned.
 * If it is a valid range (X-Y format, both are numbers, and min is less than max), max will be a valid number. Otherwise, it will be null.
 * null is returned if we could not determine any valid input.
 */
export function parseExactOrRange(input, numericType = 'decimal', allowRange = true) {
    const values = input.split('-');
    if (values.length > 2) {
        return null;
    }
    if (values.length !== 1 && !allowRange) {
        return null;
    }
    const [minRaw, maxRaw] = values;
    const parseFn = numericType === 'integer' ? parseIntNoNaN : parseFloatNoNaN;
    const min = parseFn(minRaw);
    const max = parseFn(maxRaw);
    // Found something like "10-" or "10-asm"
    if (maxRaw !== undefined && max === null) {
        return null;
    }
    // Min/exact value must be valid
    if (!isValidNumber(min)) {
        return null;
    }
    // Valid range
    if (isValidNumber(max) && min <= max) {
        return { min, max };
    }
    // Valid exact value
    return { min, max: null };
}
/**
 * Whether a metric may be prescribed as a `%` of an athlete's KPI benchmark.
 *
 * The `units.includes('%')` clause is a transition aid for metrics that have
 * not yet been migrated to the `isPercent` flag by the SC-4050 data rollout.
 * It can be removed once that rollout completes and every percent-prescribable
 * metric carries `isPercent: true`.
 */
export function metricAllowsPercent(config) {
    return config.isPercent === true || config.units.includes('%');
}
/**
 * The unit options to offer for a metric, given whether the list is being built
 * for actuals collection or for prescription.
 *
 * `%` is a prescription-only unit: it is appended to prescription lists for any
 * metric that allows it, and dropped from actuals lists. The append matters
 * because the SC-4050 rollout *removes* the literal `'%'` from `config.units`
 * and records the capability in `isPercent` instead, so a percent-prescribable
 * metric would otherwise lose the `%` option it had before the rollout ran
 * (SC-4161). Appending rather than prepending keeps `units[0]`, which callers
 * read as a metric's default unit, pointing at a real unit.
 *
 * A stored `%` (a metric the rollout has not reached) is dropped and re-appended
 * rather than left in place, so an un-migrated metric's list reads exactly like a
 * migrated one's.
 *
 * Mirrors the `acceptedUnits` set in `parseMetricValue` so a picker can never
 * offer a unit the parser rejects, or omit one it accepts. Shared by the web and
 * tablet metric hooks so the two unit dropdowns cannot drift apart.
 */
export function getPrescribableUnits(config, forActuals) {
    // `%` is never a stored unit after the rollout, and a metric that still
    // carries one is percent-capable by definition (see `metricAllowsPercent`), so
    // dropping it here costs nothing and leaves `isPercent` as the only thing that
    // decides whether `%` is offered.
    const units = config.units.filter((unit) => unit !== '%');
    if (forActuals || !metricAllowsPercent(config)) {
        return units;
    }
    return [...units, '%'];
}
/**
 * Generic parser for any value that can be a single value or a range and can possibly have a unit
 */
export function parseMetricValue(input, config, ctx) {
    if (input === '')
        return '';
    const { numericType, allowRange, units, keywords } = config;
    if (keywords.includes(input))
        return { type: 'keyword', value: input };
    if (numericType === 'none') {
        // If this isn't a numeric metric, then the value *has* to be a keyword
        return invalidValue(ctx, tKey('valueShouldBeKeyword', { keywords: keywords.join(', ') }));
    }
    const unit = (input.match(/[^0-9.-]*$/) || [''])[0].trim();
    // Percent-prescribable metrics accept `%` as a unit even when it is absent
    // from their declared `units` list, so union it into the set both branches
    // below check against (keeps the empty-units branch from rejecting `%`).
    const acceptedUnits = metricAllowsPercent(config) && !units.includes('%')
        ? [...units, '%']
        : units;
    if (units.length > 0 && !acceptedUnits.includes(unit)) {
        return invalidValue(ctx, tKey('unitShouldBe', { units: acceptedUnits.join(', ') }));
    }
    if (units.length === 0 && unit && !acceptedUnits.includes(unit)) {
        // A metric that declares no units still accepts `%` when it is
        // percent-prescribable, so name that unit rather than claiming the metric
        // takes none at all.
        return invalidValue(ctx, acceptedUnits.length > 0
            ? tKey('unitShouldBe', { units: acceptedUnits.join(', ') })
            : tKey('metricShouldNotHaveUnit'));
    }
    const value = removeUnits(input);
    const parseResult = parseExactOrRange(value, numericType, allowRange);
    if (!parseResult) {
        const numType = {
            numType: numericType === 'integer' ? 'whole number' : 'decimal',
        };
        let message;
        if (allowRange) {
            message =
                units.length > 0
                    ? tKey('valueShouldBePositiveRangeUnit', numType)
                    : tKey('valueShouldBePositiveRange', numType);
        }
        else {
            message =
                units.length > 0
                    ? tKey('valueShouldBePositiveUnit', numType)
                    : tKey('valueShouldBePositive', numType);
        }
        return invalidValue(ctx, message);
    }
    const { min, max } = parseResult;
    // range case
    if (isValidNumber(max)) {
        return {
            type: 'range',
            min,
            max,
            unit,
        };
    }
    // exact case
    return {
        type: 'exact',
        value: min,
        unit,
    };
}
// Convert exercise metrics to set metrics, setting any default values
export function getDefaultSetMetrics(metrics) {
    return metrics.map((m) => {
        return {
            id: v4(),
            metricId: m.metricId,
            input: m.metricType === 'sets' ? '1' : '',
            forCollection: false,
        };
    });
}
//# sourceMappingURL=util.js.map